import {keys} from 'ts-transformer-keys';

import {
    DataAggregator,
    DataDescriptor
} from '~/shared/src/utilities/dataStructure';
import {
    Messages,
    Styles
} from '~/shared/src/utilities/dataExtraction';


type RequiredKeys<T> = { [K in keyof T]-? : {} extends Pick<T, K> ? never : K }[keyof T];
type RequiredOf<T> = Pick<T, RequiredKeys<T>>;


export type ChartDataSetType = 'bar' | 'line';
export type LineStepMode = 'after' | 'before' | 'middle';

/*** CFG:ChartDataSet ***/
export type ChartDataSet = {
    data : {
        id : number;
        key : string;
        name : string;

        order? : number;

        backgroundColor? : string; // not set -> auto coloring
        borderColor? : string; // not set -> auto coloring

        convertBarsToDataSets: boolean;
        horizontal? : boolean; // only applicable if ChartType is 'bar'
        lineFill? : boolean; // only applicable if ChartType is 'line' | 'radar'
        lineSmooth? : boolean; // only applicable if ChartType is 'line'
        lineStepMode? : LineStepMode; // only applicable if ChartType is 'line'
        type? : ChartDataSetType; // only applicable if ChartType is 'bar' | 'line'
        stackGroup? : string; // only applicable if ChartType is 'bar' | 'line'

        aggregateFunction? : DataAggregator;
        attributePath? : string;
        queryFieldName? : string;
    };
    scale?: string;
    dataDescriptor? : DataDescriptor;
    dataDescriptors : DataDescriptor[];
    messages? : Messages; // label, popupTitle
    permissions? : GroupPermissions;
    styles? : Styles;
};

export const chartDataSetDataProps = keys<ChartDataSet['data']>();
export const chartDataSetProps = keys<ChartDataSet>();
export const requiredChartDataSetProps = keys<RequiredOf<ChartDataSet>>();


/*
HINT: map date data for timelines to milliseconds

const data: {label: string, <dataDescriptorAlias1>: number, <dataDescriptorAlias2>: number} = [
          {label: 'Sales', <dataDescriptorAlias1>: 20, <dataDescriptorAlias2>: 35},
          {label: 'Revenue', <dataDescriptorAlias1>: 10, <dataDescriptorAlias2>: 50}
      ],
      chartData: ChartData = {
          type: 'bar',
          labels: data.map( d => return d.label ),
          datasets: [
              {
                  data: data.map( d => return d.<dataDescriptorAlias1> ),

                  // for all ChartTypes
                  backgroundColor: ...,
                  borderColor: ...,

                  // only applicable for ChartType 'line'
                  fill: false, // boolean
                  stepped: undefined, // undefined (false) | 'before' (true) | 'after' | 'middle',
                  //
                  cubicInterpolationMode: undefined, // implemented via LineSmooth - undefined ('default') | 'monotone' | false
                  tension: 0.4, // implemented via LineSmooth - 0-1
                  //
                  pointStyle: undefined // not implemented - undefined ("circle") | "cross" | "crossRot" | "dash" | "line" | "rect" | "rectRounded" | "rectRot" | "star" | "triangle" | false (| HTMLImageElement | HTMLCanvasElement)
                  borderDash: undefined // not implemented
              },
              {
                  data: data.map( d => return d.<dataDescriptorAlias2> ),
                  backgroundColor: ...,
                  borderColor: ...
              }
          ]
      },
      chartOptions = {
          parsing: false,
          plugins: {
              legend: {
                  position: 'bottom',
                  align: 'start'
              },
              tooltip: {
                  position: 'nearest'
              }
          },
          responsive: true,
          interaction: {
              intersect: false
          },

          // only applicable for ChartType 'bar'

          // horizontal
          options: {
              indexAxis: 'y'
          },

          // stacked
          scales: {
              x: {
                  stacked: true,
              },
              y: {
                  stacked: true
              }
          }


      };
*/
